import React from "react";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme, styled, Stack, Box, Avatar, Typography, Button, useMediaQuery, Alert } from "@mui/material";
import { CloudUploadTwoTone } from "@mui/icons-material";
import { defaultSettings, ImageSettingsStateType, ImageInfoType } from "./ImageSettings";

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function ImageViewer(props: {
  currentState: ImageSettingsStateType,
  imageData?: string | ArrayBuffer | null,
  widgetRef: { width: number, height: number }
}) {
  const theme = useTheme();

  const imageContainerStyle = {
    position: 'relative',
    height: '240px',
    borderRadius: theme.general.borderRadius,
    backgroundColor: `${theme.colors.primary.lighter}`,
    overflow: 'hidden',
  };

  const scaleWidth = 200 / props.widgetRef.width;
  const scaleHeight = 200 / props.widgetRef.height;
  const scaleFactor = Math.min(scaleWidth, scaleHeight);
  const exampleWidth = props.widgetRef.width * scaleFactor;
  const exampleHeight = props.widgetRef.height * scaleFactor;

  const backgroundImageStyle = {
    width: `${exampleWidth}px`,
    height: `${exampleHeight}px`,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundRepeat: 'no-repeat',
    backgroundImage: props.imageData ? `url(${props.imageData})` : '',
    backgroundSize: props.currentState?.imageSize || 'contain',
    backgroundPosition: props.currentState.imagePosition,
    borderRadius: theme.general.borderRadius,
    border: `1px solid ${theme.palette.action.disabled}`,
    backgroundColor: `${props.currentState.backgroundColor}`,
  };

  return (
    <Box sx={imageContainerStyle}>
      <Box sx={backgroundImageStyle} />
    </Box>
  )
}

interface ImagePreviewProps {
  imageBase64: string | ArrayBuffer | null;
  settingsState: ImageSettingsStateType;
  updateImageBase64: (imageData: string | ArrayBuffer | null) => void;
  handleSavedImageInfo: (imageData: ImageInfoType | null) => void;
  savedImageInfo: ImageInfoType | null;
  widgetRef: { width: number, height: number };
}

export default function ImagePreview(props: ImagePreviewProps) {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const reader = new FileReader();
  const dropBoxRef = useRef();

  const currentState = {...defaultSettings, ...props.settingsState};

  const imageStyle = {
    position: 'relative',
    borderRadius: theme.general.borderRadius,
  };

  const imageDragDropStyle = {
    ...imageStyle,
    border: `2px dashed ${theme.palette.secondary.main}`,
    mb: 1,
    p: 2,
    backgroundColor: alpha(theme.palette.primary.light, 0.2),
  };

  const isDraggingStyle = {
    ...imageStyle,
    border: '2px solid transparent',
    mb: 1,
    p: 2,
    backgroundColor: theme.palette.primary.light,
  };

  useEffect(() => {
    window.addEventListener('drop', e => e.preventDefault(), false);
    window.addEventListener('dragover', e => e.preventDefault(), false);
  });

  const handleReaderEvent = (event: ProgressEvent<FileReader>) => {
    if (event.type === 'error') { /* Do we want to handle this? */ }
    // If we need to track the progress or start of reading the file ⬇
    // if (event.type === 'loadstart') handle 'loadstart'
    // if (event.type === 'progress') handle 'progress'
    if (event.type === 'load') props.updateImageBase64(reader.result);
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    const file = event.target.files[0];
    const url = URL.createObjectURL(file);
    const { name, size } = file;

    reader.readAsDataURL(file);
    reader.addEventListener('error', handleReaderEvent);
    // If we need to track the progress or start of reading the file ⬇
    // reader.addEventListener('loadstart', handleReaderEvent)
    // reader.addEventListener('progress', handleReaderEvent)
    reader.addEventListener('load', handleReaderEvent);

    props.handleSavedImageInfo({ url, name, size });
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (dropBoxRef.current === event.target) setIsDragging(true);
  };
  
  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (dropBoxRef.current === event.target) {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    if (!event.dataTransfer.files) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    
    const file = event.dataTransfer.files[0];
    const url = URL.createObjectURL(file);
    const { name, size } = file;
    
    reader.readAsDataURL(file);
    reader.addEventListener('error', handleReaderEvent);
    reader.addEventListener('load', handleReaderEvent);
    setIsDragging(false);

    props.handleSavedImageInfo({ url, name, size });
  };

  function UploadImageBox() {
    const innerBoxStyle = {
      pointerEvents: isDragging ? "none" : "",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '200px',
      justifyContent: 'center',
      p: 2
    };

    return (
      <Box
        ref={dropBoxRef}
        className={isDragging ? 'dragging' : ''}
        sx={isDragging ? isDraggingStyle : imageDragDropStyle}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <Box sx={innerBoxStyle}>
          <Avatar variant="rounded" sx={{ p: isDragging ? 5 : 3 }}>
            <CloudUploadTwoTone fontSize="large" />
          </Avatar>
          {!isDragging && 
            <>
              <Box sx={{ textAlign: "center", mb: 2, mt: 2 }}>
                <Typography
                  component="p"
                  variant="h4"
                  sx={{ fontWeight: "normal" }}>
                    {i18n.t('widget-types.image.settings.drag-drop')}
                </Typography>
              </Box>
              <Button component="label" variant="contained">
                {i18n.t('widget-types.image.settings.upload')}
                <VisuallyHiddenInput
                  type="file"
                  accept=".pdf, .png, .jpg, .jpeg, .gif"
                  onChange={handleInputChange} />
              </Button>
            </>}
        </Box>
      </Box>
    )
  }

  return (
    <Stack
      direction="column"
      sx={{
        gap: 1,
        width: isSmallScreen ? "100%" : "50%"
      }}>
      {props.savedImageInfo
        ? <>
            <Alert severity="warning">{i18n.t("widget-types.image.settings.image-dimension-warning")}</Alert>
            <ImageViewer widgetRef={props.widgetRef} currentState={currentState} imageData={props.imageBase64 ?? props.savedImageInfo?.url} />
          </>
        : <UploadImageBox />}
    </Stack>
  );
}